/**
 * The order the goods run across the bake sheet.
 *
 * The sheet is read left to right by somebody who has read the same
 * spreadsheet for a year, so the column order is a working arrangement, not a
 * presentation detail — flavours that share a dough stand together, the things
 * baked first come first.
 *
 * That order lives in `products.sheet_order`, which a migration filled once for
 * the products that existed then and which nothing has written since. So every
 * product added afterwards carried no number at all and sorted to the very end
 * of the sheet, with no way to move it. This is that missing control.
 *
 * It renumbers the whole list from what is on screen rather than swapping two
 * products' numbers, because most of them have no number to swap — the ones
 * added since are all equally "last", and exchanging two nothings moves
 * nothing. Writing positions back from the visible order gives every column a
 * real place on the way past.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { api } from '../lib/api.js';
import type { BakeSheetProduct } from '@bakery/shared';
import { Button, Modal, useToast } from '../components/ui.js';

export function SheetColumnsModal({
  products,
  onClose,
}: {
  products: BakeSheetProduct[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  /*
   * Worked on locally and written when you are done.
   *
   * Moving a column is a dozen small steps — up, up, up past the loaves — and
   * saving each one would be a dozen round trips and a dozen chances to end up
   * half-moved on a shop connection that drops.
   */
  const [order, setOrder] = useState(() => products.map((p) => ({ id: p.productId, label: p.label })));
  const original = useMemo(() => products.map((p) => p.productId).join('|'), [products]);
  const changed = order.map((p) => p.id).join('|') !== original;

  const move = (index: number, by: -1 | 1) => {
    const target = index + by;
    if (target < 0 || target >= order.length) return;
    setOrder((current) => {
      const next = [...current];
      [next[index], next[target]] = [next[target]!, next[index]!];
      return next;
    });
  };

  const save = useMutation({
    mutationFn: async () => {
      // Positions start at 1: the column order reads as first, second, third,
      // and a zero would be indistinguishable from the blank it replaces.
      for (const [position, product] of order.entries()) {
        await api.catalog.updateProduct(product.id, { sheetOrder: position + 1 });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bake-sheet'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.push({ tone: 'ok', title: 'Column order saved' });
      onClose();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not save the order', body: error.message }),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Order of the columns"
      description="How the goods run across the sheet, left to right. Anything added recently sits at the end until you move it."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={save.isPending} disabled={!changed} onClick={() => save.mutate()}>
            Save order
          </Button>
        </>
      }
    >
      <ul className="divide-y divide-[var(--color-line)]">
        {order.map((product, index) => (
          <li key={product.id} className="flex items-center gap-3 py-1.5">
            <span className="tabular w-6 shrink-0 text-right text-xs text-[var(--color-ink-faint)]">{index + 1}</span>
            <span className="min-w-0 flex-1 truncate text-sm font-medium">{product.label}</span>
            <div className="flex shrink-0 flex-col">
              <button
                type="button"
                disabled={index === 0}
                onClick={() => move(index, -1)}
                title={`Move ${product.label} left`}
                aria-label={`Move ${product.label} left`}
                className="rounded-[var(--radius-sm)] px-1 text-[var(--color-ink-faint)] transition hover:bg-[var(--color-surface-sunken)] hover:text-[var(--color-ink)] disabled:pointer-events-none disabled:opacity-25"
              >
                <ChevronUp className="size-3.5" />
              </button>
              <button
                type="button"
                disabled={index === order.length - 1}
                onClick={() => move(index, 1)}
                title={`Move ${product.label} right`}
                aria-label={`Move ${product.label} right`}
                className="rounded-[var(--radius-sm)] px-1 text-[var(--color-ink-faint)] transition hover:bg-[var(--color-surface-sunken)] hover:text-[var(--color-ink)] disabled:pointer-events-none disabled:opacity-25"
              >
                <ChevronDown className="size-3.5" />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </Modal>
  );
}
